<template>
  <div class="modal-card" style="width: 700px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="chart-pie"></b-icon>
        {{ title }}
      </p>
    </header>
      
      <section class="modal-card-body">
        <!-- Información del académico -->
        <div class="notification is-info is-light mb-4">
          <p class="has-text-weight-bold">{{ academicName }}</p>
          <p class="is-size-7">
            El académico declaró actividades en {{ areas.length }} área(s) académica(s).
            Asigne un porcentaje de ponderación a cada área.
          </p>
        </div>

        <!-- Tabla de ponderaciones -->
        <div class="box">
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>Área Académica</th>
                <th class="has-text-centered" style="width: 120px">Actividades</th>
                <th class="has-text-centered" style="width: 150px">Ponderación (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(area, index) in localWeights" :key="area.academic_area_id">
                <td>
                  <b-icon icon="book-open" size="is-small"></b-icon>
                  {{ area.area_name }}
                </td>
                <td class="has-text-centered">
                  <b-tag type="is-info">{{ area.activities_count }}</b-tag>
                </td>
                <td>
                  <b-field>
                    <b-numberinput
                      v-model="area.weight_percentage"
                      :controls="false"
                      :min="0"
                      :max="100"
                      :step="0.01"
                      @input="validateTotal"
                      expanded
                    ></b-numberinput>
                    <p class="control">
                      <span class="button is-static">%</span>
                    </p>
                  </b-field>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="2" class="has-text-right">TOTAL:</th>
                <th class="has-text-centered">
                  <b-tag :type="totalPercentageType" size="is-medium">
                    {{ totalPercentage.toFixed(2) }}%
                  </b-tag>
                </th>
              </tr>
            </tfoot>
          </table>

          <!-- Mensaje de validación -->
          <div v-if="!isTotalValid" class="notification is-warning is-light mt-3">
            <b-icon icon="exclamation-circle" size="is-small"></b-icon>
            La suma de las ponderaciones debe ser exactamente <strong>100%</strong>.
            <br>
            Actual: <strong>{{ totalPercentage.toFixed(2) }}%</strong>
          </div>

          <div v-else class="notification is-success is-light mt-3">
            <b-icon icon="check-circle" size="is-small"></b-icon>
            Las ponderaciones suman <strong>100%</strong> correctamente.
          </div>
        </div>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <b-button @click="cancel" :disabled="isSaving">
          Cancelar
        </b-button>
        <b-button
          type="is-primary"
          @click="save"
          :loading="isSaving"
          :disabled="!isTotalValid"
        >
          <b-icon icon="save"></b-icon>
          <span>{{ isEdit ? 'Actualizar Ponderaciones' : 'Guardar y Continuar' }}</span>
        </b-button>
      </footer>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AreaWeightsModal',
  
  props: {
    performanceId: {
      type: Number,
      required: true
    },
    academicName: {
      type: String,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      areas: [],
      localWeights: [],
      isSaving: false
    };
  },

  mounted() {
    this.loadAreas();
  },

  computed: {
    title() {
      return this.isEdit 
        ? 'Editar Ponderaciones por Área Académica'
        : 'Asignar Ponderaciones por Área Académica';
    },

    totalPercentage() {
      return this.localWeights.reduce((sum, area) => {
        return sum + (parseFloat(area.weight_percentage) || 0);
      }, 0);
    },

    isTotalValid() {
      const diff = Math.abs(this.totalPercentage - 100);
      return diff < 0.01; // Tolerancia de 0.01%
    },

    totalPercentageType() {
      if (this.isTotalValid) return 'is-success';
      if (this.totalPercentage > 100) return 'is-danger';
      return 'is-warning';
    }
  },

  methods: {
    async loadAreas() {
      try {
        // Cargar áreas disponibles
        const areasResponse = await axios.get(
          `/performance_precalifications/${this.performanceId}/available_areas`
        );
        
        this.areas = areasResponse.data.areas;

        // Cargar ponderaciones existentes (si las hay)
        const weightsResponse = await axios.get(
          `/performance_precalifications/${this.performanceId}/area_weights`
        );

        if (weightsResponse.data.has_weights) {
          // Modo edición: usar ponderaciones existentes
          this.localWeights = weightsResponse.data.weights.map(w => ({
            academic_area_id: w.academic_area_id,
            area_name: w.area_name,
            weight_percentage: w.weight_percentage,
            activities_count: this.areas.find(a => a.id === w.academic_area_id)?.activities_count || 0
          }));
        } else {
          // Modo creación: distribuir equitativamente
          const evenDistribution = 100 / this.areas.length;
          this.localWeights = this.areas.map(area => ({
            academic_area_id: area.id,
            area_name: area.name,
            weight_percentage: parseFloat(evenDistribution.toFixed(2)),
            activities_count: area.activities_count
          }));

          // Ajustar el último para que sume exactamente 100%
          if (this.localWeights.length > 0) {
            const sum = this.localWeights.slice(0, -1).reduce((s, w) => s + w.weight_percentage, 0);
            this.localWeights[this.localWeights.length - 1].weight_percentage = 
              parseFloat((100 - sum).toFixed(2));
          }
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        this.$buefy.toast.open({
          message: 'Error al cargar las áreas académicas',
          type: 'is-danger',
          duration: 3000
        });
      }
    },

    validateTotal() {
      // Método llamado cuando cambia algún valor
      // La validación se hace automáticamente en computed
    },

    async save() {
      if (!this.isTotalValid) {
        this.$buefy.toast.open({
          message: 'La suma debe ser exactamente 100%',
          type: 'is-warning',
          duration: 3000
        });
        return;
      }

      this.isSaving = true;

      try {
        const endpoint = this.isEdit
          ? `/performance_precalifications/${this.performanceId}/update_area_weights`
          : `/performance_precalifications/${this.performanceId}/set_area_weights`;

        const method = this.isEdit ? 'put' : 'post';

        const response = await axios[method](endpoint, {
          weights: this.localWeights.map(w => ({
            academic_area_id: w.academic_area_id,
            weight_percentage: w.weight_percentage
          }))
        });

        if (response.data.success) {
          this.$buefy.toast.open({
            message: response.data.message || 'Ponderaciones guardadas correctamente',
            type: 'is-success',
            duration: 3000
          });

          this.$emit('saved', response.data);
          this.$parent.close();
        } else {
          throw new Error(response.data.error || 'Error al guardar ponderaciones');
        }
      } catch (error) {
        console.error('Error al guardar ponderaciones:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Error al guardar ponderaciones',
          type: 'is-danger',
          duration: 5000
        });
      } finally {
        this.isSaving = false;
      }
    },

    cancel() {
      this.$parent.close();
    }
  }
};
</script>

<style scoped>
.table th {
  background-color: #f5f5f5;
}

.table tfoot th {
  border-top: 2px solid #dbdbdb;
  font-size: 1.1em;
}
</style>
